import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import { logoutUser } from './authService';
import { CollectionTriggerCreate, CollectionTriggerUpdate } from '@dill/dill-shared';

export const getSupplierCollectionTriggers = createAsyncThunk(
  'collectionTriggers/getSupplierCollectionTriggers',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionTriggers/getSupplierCollectionTriggers`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createCollectionTrigger = createAsyncThunk(
  'collectionTriggers/createCollectionTrigger',
  async ({ supplierId, data }: { supplierId: string; data: CollectionTriggerCreate }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionTriggers/createCollectionTrigger`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const updateCollectionTrigger = createAsyncThunk(
  'collectionTriggers/updateCollectionTrigger',
  async (
    {
      supplierId,
      triggerId,
      data,
    }: { supplierId: string; triggerId: string; data: CollectionTriggerUpdate },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionTriggers/${triggerId}/updateCollectionTrigger`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const deleteCollectionTrigger = createAsyncThunk(
  'collectionTriggers/deleteCollectionTrigger',
  async ({ supplierId, triggerId }: { supplierId: string; triggerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionTriggers/${triggerId}/deleteCollectionTrigger`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

// Customer-page "Add to this trigger" action
export const addCollectionTriggerTarget = createAsyncThunk(
  'collectionTriggers/addCollectionTriggerTarget',
  async (
    { supplierId, triggerId, buyerId }: { supplierId: string; triggerId: string; buyerId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionTriggers/${triggerId}/buyer/${buyerId}/addTarget`,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

// Customer-page "Remove from this trigger" action
export const removeCollectionTriggerTarget = createAsyncThunk(
  'collectionTriggers/removeCollectionTriggerTarget',
  async (
    { supplierId, triggerId, buyerId }: { supplierId: string; triggerId: string; buyerId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionTriggers/${triggerId}/buyer/${buyerId}/removeTarget`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);
